import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Globe } from "lucide-react";
import { useApp } from "@/store";
import { api, formatApiErrorDetail } from "@/lib/api";

export default function Login() {
  const { login, t } = useApp();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [suspended, setSuspended] = useState(false);
  const [unverified, setUnverified] = useState(null);
  const [resending, setResending] = useState(false);

  const resend = async () => {
    setResending(true);
    try { await api.post("/auth/resend-verification"); toast.success(t("verify_resent")); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setResending(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password);
      if (u && !u.email_verified && u.role !== "admin") { setUnverified(u.email); toast.warning(t("login_unverified_title")); }
      else { toast.success("Welcome back!"); navigate("/"); }
    }
    catch (err) {
      if (err.response?.status === 403) { setSuspended(true); toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
      else toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Login failed");
    }
    finally { setLoading(false); }
  };

  const google = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/profile";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="mx-auto flex max-w-md flex-col px-5 py-16">
      <div className="mb-8 flex items-center gap-2 font-display text-2xl font-extrabold"><span className="grid h-9 w-9 place-items-center rounded-lg bg-[#002FA7] text-white"><Globe size={18} /></span> RentGlobe</div>
      <h1 className="font-display text-3xl font-extrabold tracking-tight">{t("nav_login")}</h1>
      <form onSubmit={submit} className="mt-6 space-y-4">
        <input data-testid="login-email" type="email" required placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)}
          className="w-full rounded-xl border border-gray-200 px-4 py-3 outline-none transition-colors focus:border-[#002FA7] focus:ring-2 focus:ring-[#002FA7]/20" />
        <input data-testid="login-password" type="password" required placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)}
          className="w-full rounded-xl border border-gray-200 px-4 py-3 outline-none transition-colors focus:border-[#002FA7] focus:ring-2 focus:ring-[#002FA7]/20" />
        <button data-testid="login-submit" disabled={loading} className="w-full rounded-full bg-[#002FA7] py-3.5 font-bold text-white transition-colors hover:bg-[#002280] disabled:opacity-60">{loading ? "…" : t("nav_login")}</button>
        <p className="text-right text-sm"><Link to="/forgot-password" data-testid="login-forgot-link" className="font-semibold text-[#002FA7] hover:underline">{t("forgot_link")}</Link></p>
      </form>
      {unverified && (
        <div data-testid="login-unverified" className="mt-5 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
          <div className="font-bold">{t("login_unverified_title")}</div>
          <p className="mt-1">{t("login_unverified_body")} <b>{unverified}</b></p>
          <div className="mt-3 flex flex-wrap gap-2">
            <button data-testid="login-resend-verification" disabled={resending} onClick={resend} className="rounded-full bg-amber-900 px-4 py-1.5 text-xs font-bold text-white disabled:opacity-60">{t("verify_resend")}</button>
            <button data-testid="login-continue-anyway" onClick={() => navigate("/")} className="rounded-full border border-amber-900/40 px-4 py-1.5 text-xs font-bold text-amber-900">{t("login_continue")}</button>
          </div>
        </div>
      )}
      <div className="my-5 flex items-center gap-3 text-xs uppercase tracking-widest text-gray-300"><span className="h-px flex-1 bg-gray-200" />or<span className="h-px flex-1 bg-gray-200" /></div>
      <button data-testid="login-google" onClick={google} className="w-full rounded-full border border-gray-300 py-3.5 font-bold text-gray-700 transition-colors hover:bg-gray-50">Continue with Google</button>
      <p className="mt-6 text-center text-sm text-gray-500">No account? <Link to="/register" className="font-bold text-[#002FA7]">{t("nav_register")}</Link></p>
      {suspended && <p data-testid="login-suspended-hint" className="mt-3 rounded-xl bg-[#FF3B30]/10 p-3 text-center text-sm text-[#FF3B30]">{t("suspended_hint")} <Link to="/appeal" data-testid="login-appeal-link" className="font-bold underline">{t("suspended_link")}</Link></p>}
    </div>
  );
}
